import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  Button,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  Cancel as CancelIcon,
  PictureAsPdf,
  Print as PrintIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import nfcomService, { NotaNfcom } from '../../services/nfcomService';
import api from '../../services/useApi';
import { formatarDataHora } from '../../utils/formatToView';
import invoiceService from '@/services/invoiceService';
import { tableHeadRowSx, themedTableContainerSx } from '../../styles/theme';

interface Props {
  clientId: number;
}

const statusColor = (status: string) => {
  switch (status) {
    case 'autorizado':
      return 'success';
    case 'rejeitado':
      return 'error';
    case 'cancelado':
      return 'warning';
    case 'processando':
      return 'info';
    default:
      return 'default';
  }
};

const ClientNfcomNotasTable: React.FC<Props> = ({ clientId }) => {
  const theme = useTheme();
  const [notas, setNotas] = useState<NotaNfcom[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const carregar = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nfcomService.getByClienteId(clientId);
      setNotas(data);
    } catch (err: any) {
      console.error('Erro ao carregar NFCom do cliente:', err);
      setError('Erro ao carregar notas fiscais do cliente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);  

  const handleImprimirDanfe = async (notaId: number) => {
    try {
      Swal.fire({
        title: 'Aguarde',
        text: 'Gerando DANFE...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });

      const token = localStorage.getItem('sysup_token');
      if (!token) {
        Swal.close();
        //mostrarSnackbar('Usuário não autenticado', 'error');
        return;
      }

      const baseURL = api.defaults.baseURL;
      const response = await fetch(`${baseURL}/notas-nfcom/${notaId}/imprimir-danfe`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Falha ao gerar DANFE');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      Swal.close();
    } catch (error: any) {
      console.error('Erro ao imprimir DANFE:', error);
      Swal.close();
      //mostrarSnackbar(error.message || 'Erro ao gerar DANFE', 'error');
    }
  };
  
  const handleImprimirFatura = async (faturaId: number) => {
    try {
      Swal.fire({
        title: 'Aguarde',
        text: 'Gerando PDF...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });

      const blob = await invoiceService.imprimirPDF([faturaId], 'fatura');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.click();

      Swal.close();
    } catch (error) {
      console.error('Erro ao imprimir PDF:', error);
      Swal.close();
      //mostrarSnackbar('Erro ao gerar PDF da fatura', 'error');
    }
  };

  const handleCancelarNota = async (id: number) => {
    const confirmacao = await Swal.fire({
      title: 'Confirmar cancelamento?',
      text: 'Deseja realmente cancelar esta nota?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Voltar'
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    Swal.fire({
      title: 'Aguarde',
      text: 'Aguarde cancelando...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await api.put(`/notas-nfcom/${id}/cancelar`);
      Swal.close();
      await Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: (response && response.data && (response.data.message || response.data.msg)) || 'Nota cancelada com sucesso'
      });
      carregar();
    } catch (error: any) {
      console.error('Erro ao cancelar nota:', error);
      Swal.close();
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || 'Erro ao cancelar nota'
      });
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Notas NFCom do Cliente</Typography>
        <Button onClick={carregar} startIcon={<RefreshIcon />} variant="outlined" size="small">
          Atualizar
        </Button>
      </Stack>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!loading && !error && (
        <TableContainer component={Paper} sx={themedTableContainerSx(theme)}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx(theme)}>
                <TableCell>ID</TableCell>
                <TableCell>Número/Série</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Fatura ID</TableCell>
                <TableCell>Emissão</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma nota encontrada para este cliente.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {notas.map((nota) => (
                <TableRow key={nota.id} hover>
                  <TableCell>{nota.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{nota.numero
                      ? nota.numero.toString().padStart(9, '0').replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3')
                      : ''} / {nota.serie ? nota.serie.toString().padStart(3, '0') : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">{nota.chave_acesso || '-'}</Typography>
                  </TableCell>
                  <TableCell>R$ {Number(nota.valor_total || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{nota.fatura_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {nota.data_autorizacao ? formatarDataHora(nota.data_autorizacao) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={nota.status} color={statusColor(nota.status) as any} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Imprimir DANFE">
                      <IconButton
                        size="small"
                        onClick={() => handleImprimirDanfe(nota.id)}
                        color="secondary"
                      >
                        <PictureAsPdf />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Imprimir Fatura">
                      <IconButton
                        size="small"
                        onClick={() => handleImprimirFatura(nota.fatura_id)}
                        color="primary"
                      >
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancelar">
                      <IconButton size="small" onClick={() => handleCancelarNota(nota.id)}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClientNfcomNotasTable;


